import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const BASE = '/chat';

export type ConversationType = 'dm' | 'group' | 'department' | 'company';

export interface ChatMember {
  id: string;
  name: string;
  department: string;
}

export interface ChatConversation {
  id: string;
  type: ConversationType;
  name: string;
  department: string;
  memberCount: number | null;
  members: ChatMember[] | null;
  isAdmin: boolean;
  lastMessageAt: string | null;
  lastMessagePreview: string;
  lastMessageByName: string;
  unreadCount: number;
}

export interface ChatReplyRef {
  id: string;
  senderName: string;
  content: string;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
  mine: boolean;
  isDeleted: boolean;
  replyTo: ChatReplyRef | null;
}

interface MessagesPage {
  messages: ChatMessage[];
  hasMore: boolean;
}

const PAGE_SIZE = 100;

export const chatKeys = {
  root: ['chat'] as const,
  conversations: ['chat', 'conversations'] as const,
  messages: (id: string | null) => ['chat', 'messages', id] as const,
  allUsers: ['chat', 'all-users'] as const,
};

export function useChatConversations() {
  return useQuery({
    queryKey: chatKeys.conversations,
    queryFn: async () =>
      (await api.get<{ conversations: ChatConversation[] }>(`${BASE}/conversations`)).data.conversations,
  });
}

// Oldest-first pagination: page 1 is the newest PAGE_SIZE messages, each
// following page asks for everything before the oldest one already loaded.
export function useChatMessages(conversationId: string | null) {
  return useInfiniteQuery({
    queryKey: chatKeys.messages(conversationId),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) params.set('before', pageParam);
      return (
        await api.get<MessagesPage>(`${BASE}/conversations/${conversationId}/messages?${params.toString()}`)
      ).data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.messages.length
        ? lastPage.messages[lastPage.messages.length - 1].createdAt
        : undefined,
    enabled: !!conversationId,
  });
}

export function messagesFromPages(data: InfiniteData<MessagesPage> | undefined): ChatMessage[] {
  if (!data) return [];
  return [...data.pages].reverse().flatMap((page) => page.messages);
}

export function useChatMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: chatKeys.root });

  const startDm = useMutation({
    mutationFn: async (userId: string) =>
      (await api.post<{ id: string; name: string }>(`${BASE}/conversations`, { type: 'dm', userId })).data,
    onSuccess: refresh,
  });

  const createGroup = useMutation({
    mutationFn: async (payload: { name: string; memberIds: string[] }) =>
      (await api.post<{ id: string; name: string }>(`${BASE}/conversations`, { type: 'group', ...payload })).data,
    onSuccess: refresh,
  });

  const sendMessage = useMutation({
    mutationFn: async ({
      conversationId,
      content,
      replyToId,
    }: {
      conversationId: string;
      content: string;
      replyToId?: string;
    }) =>
      (await api.post<ChatMessage>(`${BASE}/conversations/${conversationId}/messages`, { content, replyToId })).data,
    onSuccess: (_message, variables) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(variables.conversationId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
    },
  });

  const addMembers = useMutation({
    mutationFn: async ({ conversationId, memberIds }: { conversationId: string; memberIds: string[] }) =>
      (await api.post<{ id: string; added: number; memberCount: number }>(
        `${BASE}/conversations/${conversationId}/members`,
        { memberIds },
      )).data,
    onSuccess: refresh,
  });

  const deleteMessage = useMutation({
    mutationFn: async ({ conversationId, messageId }: { conversationId: string; messageId: string }) =>
      (await api.delete(`${BASE}/conversations/${conversationId}/messages/${messageId}`)).data,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(variables.conversationId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
    },
  });

  const markRead = useMutation({
    mutationFn: async (conversationId: string) =>
      (await api.post(`${BASE}/conversations/${conversationId}/read`, {})).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
  });

  return { startDm, createGroup, sendMessage, addMembers, deleteMessage, markRead };
}
